<script setup lang="ts">
import {computed} from "vue";
import type {ICellRendererParams} from "ag-grid-community";

type BarOptions = { min?: number; max?: number; color?: string; showValue?: boolean };
type ProgressOptions = { min?: number; max?: number; showLabel?: boolean };

const props = defineProps<{
  params: ICellRendererParams & {
    rendererOptions?: { bar?: BarOptions; progress?: ProgressOptions };
    colId?: string;
    mode?: 'bar' | 'progress';
  };
}>();

function resolveColId(): string | undefined {
  return props.params.colId ?? props.params.column?.getColId();
}

const cell = computed(() => {
  const data: any = props.params.data;
  const colId = resolveColId();
  return data?.cells?.[colId ?? ''] ?? null;
});

const numericValue = computed<number>(() => {
  const raw = cell.value?.value;
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw));
  return isNaN(n) ? 0 : n;
});

const cfg = computed(() => {
  const mode = props.params.mode ?? 'bar';
  if (mode === 'progress') {
    const p = props.params.rendererOptions?.progress ?? {};
    return {
      min: p.min ?? 0,
      max: p.max ?? 100,
      color: '#2196f3',
      showLabel: p.showLabel !== false,
      isProgress: true,
    };
  }
  const b = props.params.rendererOptions?.bar ?? {};
  return {
    min: b.min ?? 0,
    max: b.max ?? 100,
    color: b.color ?? '#4caf50',
    showLabel: b.showValue === true,
    isProgress: false,
  };
});

const fillPercent = computed(() => {
  const {min, max} = cfg.value;
  if (max === min) return 0;
  const pct = ((numericValue.value - min) / (max - min)) * 100;
  return Math.max(0, Math.min(100, pct));
});

const labelText = computed(() => {
  const dv = cell.value?.displayValue;
  if (dv != null && dv !== '') return dv;
  if (cfg.value.isProgress) return `${Math.round(fillPercent.value)}%`;
  return String(numericValue.value);
});
</script>

<template>
  <div :title="labelText"
       style="display: flex; align-items: center; gap: 6px; width: 100%; min-height: 22px; padding: 4px 0;">
    <div style="flex: 1; height: 10px; background: rgba(0,0,0,0.08); border-radius: 5px; overflow: hidden;">
      <div :style="{height: '100%', width: fillPercent + '%', backgroundColor: cfg.color, borderRadius: '5px', transition: 'width 0.2s ease-out'}"></div>
    </div>
    <span v-if="cfg.showLabel"
          style="font-size: 0.85em; font-variant-numeric: tabular-nums; white-space: nowrap;">
      {{ labelText }}
    </span>
  </div>
</template>
